import React, {FC, useEffect, useState} from 'react';
import {Button, Modal, Select, Spin, Table} from 'antd';
import {useTranslation} from 'react-i18next';

import UserPermissions from '../../model/userPermissions';
import {Enquiry} from '../../model/enquiry';
import TransactionState from '../../model/TransactionState';
import {retryEnquiry} from '../../service/RetryService';
import ConfirmRetryDialog from '../ConfirmRetryDialog/ConfirmRetryDialog';
import {getTransactionsForEnquiry} from "../../service/dao/EnquiryDao";

import './RetryEnquiryDialog.less';
import {AxiosResponse} from "axios";

const ACTION_UNSET = 'UNSET';
const ACTION_CLOSE = 'CLOSE';
const ACTION_RECLOSE = 'RECLOSE';
const ACTION_REFER = 'REFER';
const ACTION_RESEND = 'RESEND';
const ACTION_NOTIFY = 'NOTIFY';
const ACTION_CANCEL = 'CANCEL';

const NOTIFICATION_PARTIAL_UPDATE = "PARTIAL_UPDATE;"
const NOTIFICATION_VALIDATED = "VALIDATED";
const NOTIFICATION_REVIEW = "REVIEW";
const NOTIFICATION_ERRORED_ENQUIRY = "ERRORED_ENQUIRY";
const NOTIFICATION_ARRANGED = "ARRANGED";
const NOTIFICATION_CANCELLED = "CANCELLED";
const NOTIFICATION_MISSED = "MISSED";
const NOTIFICATION_REBOOKING = "REBOOKING";
const NOTIFICATION_CLOSED = "CLOSED";
const NOTIFICATION_ERRORED_EXAM = "ERRORED_EXAM";
const NOTIFICATION_NO_RESPONSE = "NO_RESPONSE";

const RetryEnquiryDialog: FC<{
  accessToken: string,
  onExit: () => void,
  open: boolean,
  permissions: UserPermissions,
  enquiry: Enquiry | null,
}> = ({
  accessToken, onExit, open, permissions, enquiry,
}) => {
  const [displayDialog, setDisplayDialog] = useState(true);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(ACTION_UNSET);
  const [notification, setNotification] = useState(ACTION_UNSET);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [medicalExams, setMedicalExams] = useState<TransactionState[]>([]);
  const [chosenMedicalExam, setChosenMedicalExam] = useState<TransactionState|null>(null);

  const { t } = useTranslation();

  const loadMedicalExams = (enquiryId: string) => {
    setBusy(true);
    getTransactionsForEnquiry(accessToken, enquiryId).then(
      (exams: AxiosResponse<TransactionState[]>) => {
        setMedicalExams(exams.data)
        setBusy(false);
      }
    );
  };

  useEffect(
      () => {
        if (enquiry && open) loadMedicalExams(enquiry.enquiryId)
        setAction(ACTION_UNSET);
        setNotification(ACTION_UNSET);
        setChosenMedicalExam(null);
      },
      [enquiry]
  );

  const handleCancel = () => {
    onExit();
  };

  const handleYes = () => {
    if (enquiry != null && action != ACTION_UNSET) {
      setConfirmationDialogOpen(true);
      setDisplayDialog(false);
    } else {
      onExit();
    }
  };

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event != null && event.target != null) {
      setAction(event.target.value);
    }
  };

  const handleExamSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event != null && event.target != null) {
      const transactionId = event.target.value;
      const medicalExam = medicalExams
        .find((medicalExam) => medicalExam.transactionId === transactionId);
      if (medicalExam) {
          setChosenMedicalExam(medicalExam);
      } else {
        setChosenMedicalExam(null);
      }
    }
  };

  const onConfirmationDialogExit = (confirmed: boolean): void => {
    if (confirmed && enquiry != null) {
      setBusy(true);
      let aggregateId;
      if (isForMedicalExam(action, notification) && chosenMedicalExam) {
        aggregateId = chosenMedicalExam.transactionId;
      } else {
        aggregateId = enquiry.enquiryId;
      }
      retryEnquiry(accessToken, aggregateId, action, notification)
          .then(
              success => {
                setBusy(false);
                setConfirmationDialogOpen(false);
                setDisplayDialog(true);
                onExit();
              },
              failure => {
                setBusy(false);
              }
          );
    } else {
      setConfirmationDialogOpen(false);
      setDisplayDialog(true);
    }
  };

  var status = '';
  var allClosed = false;
  if (enquiry != null) {
      allClosed = true;
      var statusList: string[] = [];
      enquiry.evidenceProviders.forEach((provider) => {
          let bookingStatus = provider.bookingStatus;
          statusList.push(bookingStatus);
          if (bookingStatus !== 'CLOSED') allClosed = false;
      })
      status = statusList.join(', ');
  }
  
  const notificationOptions = [
    { value: ACTION_UNSET, label: t('retry.enquiry.dialog.notification.enquiries'), disabled: true},
    { value: NOTIFICATION_PARTIAL_UPDATE, label: t('retry.enquiry.dialog.notification.PARTIAL_UPDATE')},
    { value: NOTIFICATION_VALIDATED, label: t('retry.enquiry.dialog.notification.VALIDATED')},
    { value: NOTIFICATION_REVIEW, label: t('retry.enquiry.dialog.notification.REVIEW')},
    { value: NOTIFICATION_ERRORED_ENQUIRY, label: t('retry.enquiry.dialog.notification.ERRORED_ENQUIRY')},
    { value: ACTION_UNSET, label: t('retry.enquiry.dialog.notification.medical_exams'), disabled: true},
    { value: NOTIFICATION_ARRANGED, label: t('retry.enquiry.dialog.notification.ARRANGED')},
    { value: NOTIFICATION_CANCELLED, label: t('retry.enquiry.dialog.notification.CANCELLED')},
    { value: NOTIFICATION_MISSED, label: t('retry.enquiry.dialog.notification.MISSED')},
    { value: NOTIFICATION_REBOOKING, label: t('retry.enquiry.dialog.notification.REBOOKING')},
    { value: NOTIFICATION_CLOSED, label: t('retry.enquiry.dialog.notification.CLOSED')},
    { value: NOTIFICATION_ERRORED_EXAM, label: t('retry.enquiry.dialog.notification.ERRORED_EXAM')},
    { value: NOTIFICATION_NO_RESPONSE, label: t('retry.enquiry.dialog.notification.NO_RESPONSE')},
  ];

  const isForMedicalExam =(action: string, notification: string): boolean => {
    return action === ACTION_CANCEL || (
      action === ACTION_NOTIFY
      && notification !== NOTIFICATION_PARTIAL_UPDATE
      && notification !== NOTIFICATION_VALIDATED
      && notification !== NOTIFICATION_REVIEW
      && notification !== NOTIFICATION_ERRORED_ENQUIRY
      && notification !== ACTION_UNSET
    );
  };

  const renderExamChoice = (medicalExam: TransactionState) => {
    const transactionId = medicalExam.transactionId;
    return (
      <input
        type="radio"
        name="exam_choice"
        id={transactionId}
        value={transactionId}
        onChange={handleExamSelection}
        checked={transactionId === chosenMedicalExam?.transactionId}
        disabled={!isForMedicalExam(action, notification)}
      />
    );
  };

  const medicalExamColumns: any[] = [
    {
      title: '',
      render: (medicalExam: TransactionState) => renderExamChoice(medicalExam),
    },
    {
      title: t('retry.enquiry.dialog.medical.exam.table.lastUpdated'),
      dataIndex: 'updated',
      render: (updated: Date, medicalExam: TransactionState) => updated?.toLocaleDateString()
    },
    {
      title: t('retry.enquiry.dialog.medical.exam.table.evidenceRequested'),
      dataIndex: 'evidenceRequested',
    },
    {
      title: t('retry.enquiry.dialog.medical.exam.table.evidenceProvider'),
      dataIndex: 'evidenceProvider',
    },
    {
      title: t('retry.enquiry.dialog.medical.exam.table.status'),
      dataIndex: 'bookingStatus',
    },
  ];

  return (
    <>
      {displayDialog && (
          <Modal
            className="retry-dialog"
            title={t('retry.enquiry.dialog.title')}
            onCancel={handleCancel}
            open={open}
            destroyOnHidden={true}
            footer={[
              <Button
                  key="cancel"
                  title={`${t('retry.enquiry.dialog.dialog.button.cancel.title')}`}
                  onClick={handleCancel}
                  disabled={busy || confirmationDialogOpen}
              >
                {t('retry.enquiry.dialog.dialog.button.cancel')}
              </Button>,
              <Button
                  key="yes"
                  type="primary"
                  title={`${t('retry.enquiry.dialog.dialog.button.yes.title')}`}
                  onClick={handleYes}
                  disabled={(
                    busy
                    || confirmationDialogOpen
                    || action === ACTION_UNSET
                    || (action === ACTION_NOTIFY && notification === ACTION_UNSET)
                    || (isForMedicalExam(action, notification) && !chosenMedicalExam)
                  )}
              >
                {t('retry.enquiry.dialog.dialog.button.yes')}
              </Button>,
            ]}
          >
            <div className="retry-dialog-content">
              <div className="retry-dialog-content-section">
                {t('retry.enquiry.dialog.description')}
                <table className="retry-dialog-content-section">
                  <tbody>
                    <tr>
                      <th>{t('retry.enquiry.dialog.applicationId')}</th>
                      <td>{enquiry?.applicationId}</td>
                    </tr>
                    <tr>
                      <th>{t('retry.enquiry.dialog.enquiryId')}</th>
                      <td>{enquiry?.enquiryId}</td>
                    </tr>
                    <tr>
                      <th>{t('retry.enquiry.dialog.name')}</th>
                      <td>{enquiry?.firstName} {enquiry?.surname}</td>
                    </tr>
                    <tr>
                        <th>{t('retry.enquiry.dialog.status')}</th>
                        <td>{status}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {permissions.canRetryEnquiry && (
                <div className="retry-dialog-content-section">
                  <hr />
                  <h3>{t('retry.enquiry.dialog.action.title')}</h3>
                  <input
                      type="radio"
                      name="retry_action"
                      id="action_close"
                      value={ACTION_CLOSE}
                      onChange={handleSelection}
                      checked={action === ACTION_CLOSE}
                  />
                  <label htmlFor="action_close">{t('retry.enquiry.dialog.action.CLOSE')}</label>
                  <br/>
                  <input
                      type="radio"
                      name="retry_action"
                      id="action_reclose"
                      value={ACTION_RECLOSE}
                      onChange={handleSelection}
                      checked={action === ACTION_RECLOSE}
                  />
                  <label htmlFor="action_reclose">{t('retry.enquiry.dialog.action.RECLOSE')}</label>
                  <br/>
                  <input
                      type="radio"
                      name="retry_action"
                      id="action_refer"
                      value={ACTION_REFER}
                      onChange={handleSelection}
                      checked={action === ACTION_REFER}
                  />
                  <label htmlFor="action_refer">{t('retry.enquiry.dialog.action.REFER')}</label>
                  { !allClosed && (
                    <>
                      <br/>
                      <input
                          type="radio"
                          name="retry_action"
                          id="action_resend"
                          value={ACTION_RESEND}
                          onChange={handleSelection}
                          checked={action === ACTION_RESEND}
                      />
                      <label htmlFor="action_resend">{t('retry.enquiry.dialog.action.RESEND')}</label>
                    </>
                  )}
                  <br/>
                  <input
                    type="radio"
                    name="retry_action"
                    id="action_notify"
                    value={ACTION_NOTIFY}
                    onChange={handleSelection}
                    checked={action === ACTION_NOTIFY}
                  />
                  <label htmlFor="action_notify">{t('retry.enquiry.dialog.action.NOTIFY')}</label>
                  <br/>
                  <input
                    type="radio"
                    name="retry_action"
                    id="action_cancel"
                    value={ACTION_CANCEL}
                    onChange={handleSelection}
                    checked={action === ACTION_CANCEL}
                  />
                  <label htmlFor="action_cancel">{t('retry.enquiry.dialog.action.CANCEL')}</label>
                  <h3>{t('retry.enquiry.dialog.notification.title')}</h3>
                  <Select
                    id="notify_action"
                    options={notificationOptions}
                    disabled={action !== ACTION_NOTIFY}
                    onChange={value => setNotification(value)}
                    className="retry-notification-type"
                    defaultValue={notification === ACTION_UNSET ? null : notification}
                  />
                  <h3>{t('retry.enquiry.dialog.medical.exams.title')}</h3>
                  <Table
                    columns={medicalExamColumns}
                    dataSource={medicalExams}
                    rowKey="transactionId"
                    pagination={false}
                  />
                </div>
              )}
            </div>
          </Modal>
      )}
      <ConfirmRetryDialog
          onExit={onConfirmationDialogExit}
          open={confirmationDialogOpen}
          enquiry={enquiry}
          action={action}
          notification={notification}
          medicalExam={chosenMedicalExam}
          forMedicalExam={isForMedicalExam(action, notification) && chosenMedicalExam !== null}
      />
      <div className="spinner">
        <Spin spinning={busy} />
      </div>
    </>
  );
};

export default RetryEnquiryDialog;
